import { useState } from 'react';
import { Navigate, Outlet } from 'react-router';

import { flashState } from './flash';
import { useSession } from './session';

/** Where the onboarding step that creates the account (NPI confirmation) lives. */
export const ONBOARDING_NPI_PATH = '/onboarding/npi';

const loginRedirect = (expired: boolean) => (expired ? '/login?reason=session_expired' : '/login');

/**
 * Routes for people with a portal account. Without a session: login. Without an account yet: the NPI step, told that
 * sign-in (or sign-up: Auth0 returns both to `/`) verified the user's identity, so it can say so.
 */
export const RequireAccount = () => {
  const session = useSession();
  switch (session.status) {
    case 'loading':
      return null;
    case 'signed-out':
      return (
        <Navigate
          to={loginRedirect(session.expired)}
          replace
        />
      );
    case 'needs-account':
      return (
        <Navigate
          to={ONBOARDING_NPI_PATH}
          replace
          state={flashState('identity-verified')}
        />
      );
    case 'signed-in':
      return <Outlet />;
  }
};

/**
 * The onboarding steps before the account exists. Once it does, they are done: go to the dashboard. When this visit
 * started without an account, the account was created just now, and the dashboard is told so it can welcome the user.
 * Someone who already had one and opened a step by hand goes to the dashboard without the welcome.
 */
export const RequireNoAccount = () => {
  const session = useSession();
  const [startedWithoutAccount, setStartedWithoutAccount] = useState(false);
  if (session.status === 'needs-account' && !startedWithoutAccount) {
    setStartedWithoutAccount(true);
  }
  switch (session.status) {
    case 'loading':
      return null;
    case 'signed-out':
      return (
        <Navigate
          to={loginRedirect(session.expired)}
          replace
        />
      );
    case 'needs-account':
      return <Outlet />;
    case 'signed-in':
      return (
        <Navigate
          to="/"
          replace
          state={startedWithoutAccount ? flashState('account-created') : null}
        />
      );
  }
};
